<script setup lang="ts">
import { ref } from 'vue';
import { PiButton, PiInputField, PiMessage } from '@pappers-fr/pappers-ui';
import { useAuth } from '@/composables/useAuth';
import { isDevMode } from '@/utils/devMode';

const { login, loginWithApiKey } = useAuth();
const isDev = isDevMode();
const apiKeyInput = ref(isDev ? localStorage.getItem('pappers-addon-api-key') || '' : '');
const orgIdInput = ref(isDev ? localStorage.getItem('pappers-addon-org-id') || '' : '');
const loginError = ref<string | null>(null);
const isLoading = ref(false);

const handleLogin = async () => {
	loginError.value = null;
	isLoading.value = true;
	try {
		await login();
	} catch (e: any) {
		loginError.value = e?.message || 'Erreur de connexion';
	} finally {
		isLoading.value = false;
	}
};

const handleApiKeyLogin = async () => {
	if (!apiKeyInput.value.trim() || !orgIdInput.value.trim()) return;
	loginError.value = null;
	isLoading.value = true;
	try {
		await loginWithApiKey(apiKeyInput.value.trim(), orgIdInput.value.trim());
	} catch (e: any) {
		loginError.value = e?.message || JSON.stringify(e);
	} finally {
		isLoading.value = false;
	}
};
</script>

<template>
	<div class="flex flex-col items-center justify-center h-full p-4 gap-4">
		<p class="text-base text-center font-medium">
			Complément pour avocats et juristes : gérez vos pièces, numérotez-les et générez votre bordereau depuis Word, en
			lien avec votre workspace Pappers Justice.
		</p>
		<p v-if="isDev" class="text-sm text-neutral-600 text-center">Renseignez votre clé API (mode dev)</p>
		<p v-else class="text-sm text-neutral-600 text-center">
			Connectez-vous à votre compte Pappers pour accéder à vos dossiers.
		</p>
		<PiMessage
			v-if="loginError"
			type="danger"
			:label="loginError"
			size="sm"
			has-close-button
			@close-alert="loginError = null"
		/>
		<template v-if="isDev">
			<PiInputField
				:model-value="apiKeyInput"
				placeholder="Clé API..."
				size="sm"
				@update:model-value="apiKeyInput = $event"
				@keyup.enter="handleApiKeyLogin"
			/>
			<PiInputField
				:model-value="orgIdInput"
				placeholder="Organization ID..."
				size="sm"
				@update:model-value="orgIdInput = $event"
				@keyup.enter="handleApiKeyLogin"
			/>
			<PiButton
				label="Se connecter"
				color="primary"
				size="sm"
				is-full-width
				:is-loading="isLoading"
				@click="handleApiKeyLogin"
			/>
		</template>
		<PiButton
			v-else
			label="Se connecter avec Pappers"
			color="primary"
			size="sm"
			is-full-width
			:is-loading="isLoading"
			@click="handleLogin"
		/>
		<p class="text-center">
			Pas encore de compte ?
			<a href="https://justice.pappers.fr/offres" class="text-primary-500 text-underline">Cliquez ici</a>
			pour acheter une licence Pappers Workspace
		</p>
		<p class="text-[11px] text-neutral-400 text-center">
			Certains éléments sont générés par une IA (rappel des faits, nom des pièces, etc.). L'IA peut faire des erreurs,
			vérifiez les informations fournies.
		</p>
	</div>
</template>
